import { useEffect, useState } from "react";
import { api } from "../lib/apiClient";
import { useNavigate } from "react-router-dom";
import { CheckCircle2, Trash2 } from "lucide-react";
import { fmtDateTimeIST } from "../lib/formatDateTime";
import { useMatch } from "../lib/MatchContext";
import { useAuth } from "../lib/AuthContext";

export default function MatchesPage() {
  const [matches, setMatches] = useState([]);
  const { selectedMatchId, chooseMatch, refresh } = useMatch();
  const { user } = useAuth();
  const canDelete = (user?.permissions || []).includes("admin.users");
  const nav = useNavigate();
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({
    name: "", team_a: "", team_b: "", format: "ODI", match_date: "", venue: "", city: "",
    departments: ["Security", "Medical", "Fire & Safety", "Ticketing", "Housekeeping", "Ground Staff", "Broadcast", "Hospitality", "Traffic", "Command Centre"],
  });

  const load = async () => {
    const { data } = await api.get("/matches");
    setMatches(data);
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const payload = { ...form, match_date: new Date(form.match_date).toISOString() };
    await api.post("/matches", payload);
    setShowNew(false);
    load();
    refresh();
  };

  const remove = async (m) => {
    const confirmText = `Delete "${m.name}"?\n\nThis will permanently remove the match and ALL associated data — pre-match tasks, venue items, run sheet, incidents, issues, readiness certs and stocks. This cannot be undone.`;
    if (!window.confirm(confirmText)) return;
    try {
      await api.delete(`/matches/${m.id}`);
      if (selectedMatchId === m.id) chooseMatch(null);
      load();
      refresh();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete match");
    }
  };

  return (
    <div className="p-6 md:p-10 pb-20" data-testid="page-matches">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6 border-b border-cmdBorder pb-6">
        <div>
          <div className="mc-label">Fixtures Portfolio</div>
          <h1 className="font-head font-black text-4xl md:text-5xl uppercase tracking-tight leading-none mt-2">Matches</h1>
        </div>
        <button
          onClick={() => setShowNew((v) => !v)}
          className="bg-white text-black px-4 py-2 font-bold uppercase tracking-widest text-xs"
          data-testid="match-new-btn"
        >
          {showNew ? "Cancel" : "+ New Match"}
        </button>
      </div>

      {showNew && (
        <form onSubmit={create} className="mc-panel p-5 mb-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="match-form">
          <input className="bg-black border border-cmdBorder p-2 text-sm md:col-span-3" placeholder="Match name (e.g., India vs England — 1st T20)" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <input className="bg-black border border-cmdBorder p-2 text-sm" placeholder="Team A" required value={form.team_a} onChange={(e) => setForm({ ...form, team_a: e.target.value })} />
          <input className="bg-black border border-cmdBorder p-2 text-sm" placeholder="Team B" required value={form.team_b} onChange={(e) => setForm({ ...form, team_b: e.target.value })} />
          <select className="bg-black border border-cmdBorder p-2 text-sm" value={form.format} onChange={(e) => setForm({ ...form, format: e.target.value })}>
            {["T20", "ODI", "TEST"].map((f) => <option key={f}>{f}</option>)}
          </select>
          <input type="datetime-local" className="bg-black border border-cmdBorder p-2 text-sm" required value={form.match_date} onChange={(e) => setForm({ ...form, match_date: e.target.value })} />
          <input className="bg-black border border-cmdBorder p-2 text-sm" placeholder="Venue" required value={form.venue} onChange={(e) => setForm({ ...form, venue: e.target.value })} />
          <input className="bg-black border border-cmdBorder p-2 text-sm" placeholder="City" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} />
          <button type="submit" className="bg-readyGreen text-black font-bold uppercase tracking-widest text-xs md:col-span-3" data-testid="match-form-submit">Create Match</button>
        </form>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {matches.map((m) => {
          const isSelected = m.id === selectedMatchId;
          return (
            <div
              key={m.id}
              className={`mc-panel p-5 mc-panel-hover transition-colors block ${isSelected ? "border-readyGreen" : ""}`}
              data-testid={`match-card-${m.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="font-mono-live text-xs text-zinc-500">{fmtDateTimeIST(m.match_date)} <span className="text-[10px] text-zinc-600">IST</span></div>
                <div className="text-[10px] uppercase tracking-widest font-bold border border-cmdBorder px-2 py-0.5">{m.status}</div>
              </div>
              <h3 className="font-head font-black text-3xl uppercase tracking-tight mt-3 leading-tight">{m.name}</h3>
              <div className="text-sm text-zinc-400 mt-2">{m.venue} · {m.city} · <span className="uppercase tracking-widest">{m.format}</span></div>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <button
                  onClick={() => { chooseMatch(m.id); nav("/"); }}
                  data-testid={`match-select-${m.id}`}
                  className={`px-3 py-2 text-xs uppercase tracking-widest font-bold border transition-colors flex items-center gap-1 ${
                    isSelected
                      ? "bg-readyGreen text-black border-readyGreen"
                      : "border-white text-white hover:bg-white hover:text-black"
                  }`}
                >
                  {isSelected ? <><CheckCircle2 className="w-3 h-3" /> Selected</> : "Select Match"}
                </button>
                {canDelete && (
                  <button
                    onClick={() => remove(m)}
                    data-testid={`match-delete-${m.id}`}
                    title="Delete this match and all its data"
                    className="px-2 py-2 text-xs border border-cmdBorder hover:border-incidentRed hover:text-incidentRed transition-colors flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" /> Delete
                  </button>
                )}
                {isSelected && (
                  <span className="text-xs text-zinc-500">Command Centre, Pre-Match, Match Day, Post-Match all scoped to this match.</span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
